import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const AdminCategories = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/categories/${editing.id}`, data);
      } else {
        await api.post("/categories", data);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">Categories</h1>
        <button onClick={() => setEditing({ type: "export", sector: "", name: { en: "", tr: "", id: "" }, slug: "", image_url: "", sort_order: 0 })} data-testid="add-category-btn" className="btn-gold">
          <Plus size={14} /> Add Category
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Name (EN)</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Sector</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{getMl(c.name, "en")}</td>
                <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-sm uppercase tracking-[0.15em] ${c.type === "export" ? "bg-[#D4AF37]/15 text-[#0A1128]" : "bg-slate-100 text-slate-600"}`}>{c.type}</span></td>
                <td className="px-4 py-3 text-slate-600">{c.sector || "—"}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{c.slug}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(c)} data-testid={`edit-cat-${c.id}`} className="text-slate-400 hover:text-[#0A1128] p-1.5"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(c.id)} data-testid={`delete-cat-${c.id}`} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <CategoryForm initial={editing} onSave={onSave} onClose={() => setEditing(null)} />}
    </div>
  );
};

const CategoryForm = ({ initial, onSave, onClose }) => {
  const [data, setData] = useState(initial);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6 overflow-y-auto" data-testid="category-form-modal">
      <div className="bg-white max-w-2xl w-full rounded-sm my-10">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="font-display text-2xl">{initial.id ? "Edit" : "Add"} Category</h2>
          <button onClick={onClose} data-testid="close-cat-form" className="p-2 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Type</label>
              <select value={data.type} onChange={(e) => setData({ ...data, type: e.target.value })} data-testid="cat-type" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm">
                <option value="export">Export</option>
                <option value="import">Import</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sector (Import)</label>
              <input value={data.sector || ""} onChange={(e) => setData({ ...data, sector: e.target.value })} data-testid="cat-sector" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
            </div>
          </div>
          <MultilingualField label="Name" value={data.name} onChange={(v) => setData({ ...data, name: v })} testId="cat-name" required />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Slug *</label>
              <input value={data.slug} onChange={(e) => setData({ ...data, slug: e.target.value })} required data-testid="cat-slug" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Sort Order</label>
              <input type="number" value={data.sort_order} onChange={(e) => setData({ ...data, sort_order: +e.target.value })} data-testid="cat-sort" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
            </div>
          </div>
          <FileUploader label="Category Image" value={data.image_url} onChange={(v) => setData({ ...data, image_url: v })} testId="cat-image" />
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button onClick={onClose} data-testid="cat-cancel" className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
          <button onClick={() => onSave(data)} data-testid="cat-save" className="btn-gold">Save</button>
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
